import { useEffect, useState, useRef } from "react";

import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Toolbar } from "primereact/toolbar";

import useAxiosPrivate from "../hooks/useAxiosPrivate";

const PagosHistorial = () => {

  const axiosPrivate = useAxiosPrivate();

  const dt = useRef(null);

  const [pagos, setPagos] = useState([]);

  const [loading, setLoading] = useState(false);

  //
  // 🔥 filtros
  //
  const [globalFilter, setGlobalFilter] = useState("");

  const [fechaInicio, setFechaInicio] = useState(null);

  const [fechaFin, setFechaFin] = useState(null);

  const [metodoPago, setMetodoPago] = useState(null);

  const [facturado, setFacturado] = useState(null);

  //
  // 🔥 catálogos
  //
  const metodosPago = [
    { label: "Efectivo", value: "efectivo" },
    { label: "Transferencia", value: "transferencia" },
    { label: "Tarjeta", value: "tarjeta" },
    { label: "Banco", value: "banco" }
  ];

  const facturadoOptions = [
    { label: "Facturado", value: 1 },
    { label: "No Facturado", value: 0 }
  ];

  //
  // 🔥 cargar pagos
  //
  const cargarPagos = async () => {

    try {

      setLoading(true);

      const params = {};

      if (fechaInicio) {
        params.fechaInicio = fechaInicio.toISOString().slice(0, 10);
      }

      if (fechaFin) {
        params.fechaFin = fechaFin.toISOString().slice(0, 10);
      }

      if (metodoPago) {
        params.metodo_pago = metodoPago;
      }

      if (facturado !== null) {
        params.facturado = facturado;
      }

      const res = await axiosPrivate.get(
        "CEUM/pagos",
        { params }
      );

      setPagos(res.data);

    } catch (error) {

      console.error(error);

    } finally {

      setLoading(false);

    }
  };

  //
  // 🔥 load inicial
  //
  useEffect(() => {
    cargarPagos();
  }, []);

  //
  // 🔥 export excel
  //
  const exportExcel = () => {
    dt.current.exportCSV();
  };

  //
  // 🔥 limpiar filtros
  //
  const limpiarFiltros = () => {

    setFechaInicio(null);

    setFechaFin(null);

    setMetodoPago(null);

    setFacturado(null);

    setGlobalFilter("");

    cargarPagos();
  };

  //
  // 🔥 buscar
  //
  const buscar = () => {
    cargarPagos();
  };

  //
  // 🔥 totalizador
  //
  const totalPagos = pagos.reduce(
    (acc, p) => acc + Number(p.monto || 0),
    0
  );

  //
  // 🔥 facturado template
  //
  const facturadoBody = (row) => (
    row.facturado
      ? <Tag severity="success" value="Sí" />
      : <Tag severity="danger" value="No" />
  );

  //
  // 🔥 monto template
  //
  const montoBody = (row) => (
    Number(row.monto || 0).toLocaleString(
      "es-MX",
      {
        style: "currency",
        currency: "MXN"
      }
    )
  );

  //
  // 🔥 toolbar
  //
  const leftToolbar = (
    <div className="flex gap-2">

      <Button
        label="Buscar"
        icon="pi pi-search"
        onClick={buscar}
      />

      <Button
        label="Limpiar"
        icon="pi pi-filter-slash"
        severity="secondary"
        onClick={limpiarFiltros}
      />

    </div>
  );

  const rightToolbar = (
    <div className="flex gap-2">

      <Button
        label="Exportar"
        icon="pi pi-file-excel"
        severity="success"
        onClick={exportExcel}
      />

    </div>
  );

  return (
    <Card title="Historial de Pagos">

      <Toolbar
        className="mb-3"
        left={leftToolbar}
        right={rightToolbar}
      />

      {/* 🔥 filtros */}
      <div className="grid mb-3">

        <div className="col-12 md:col-3">

          <label>Buscar</label>

          <InputText
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            placeholder="Alumno, matrícula..."
            className="w-full"
          />

        </div>

        <div className="col-12 md:col-2">

          <label>Fecha Inicio</label>

          <Calendar
            value={fechaInicio}
            onChange={(e) => setFechaInicio(e.value)}
            showIcon
            className="w-full"
          />

        </div>

        <div className="col-12 md:col-2">

          <label>Fecha Fin</label>

          <Calendar
            value={fechaFin}
            onChange={(e) => setFechaFin(e.value)}
            showIcon
            className="w-full"
          />

        </div>

        <div className="col-12 md:col-2">

          <label>Método</label>

          <Dropdown
            value={metodoPago}
            options={metodosPago}
            onChange={(e) => setMetodoPago(e.value)}
            placeholder="Todos"
            className="w-full"
          />

        </div>

        <div className="col-12 md:col-2">

          <label>Facturado</label>

          <Dropdown
            value={facturado}
            options={facturadoOptions}
            onChange={(e) => setFacturado(e.value)}
            placeholder="Todos"
            className="w-full"
          />

        </div>

      </div>

      {/* 🔥 resumen */}
      <div className="mb-3 flex justify-content-end">

        <h3>
          Total:
          {" "}
          {
            totalPagos.toLocaleString(
              "es-MX",
              {
                style: "currency",
                currency: "MXN"
              }
            )
          }
        </h3>

      </div>

      {/* 🔥 tabla */}
      <DataTable
        ref={dt}
        value={pagos}
        paginator
        rows={20}
        loading={loading}
        responsiveLayout="scroll"
        stripedRows
        showGridlines
        sortMode="multiple"
        removableSort
        globalFilter={globalFilter}
      >

        <Column
          field="fecha"
          header="Fecha"
          sortable
        />

        <Column
          field="matricula"
          header="Matrícula"
          sortable
        />

        <Column
          field="alumno"
          header="Alumno"
          sortable
        />

        <Column
          field="grado"
          header="Grado"
          sortable
        />

        <Column
          field="grupo"
          header="Grupo"
          sortable
        />

        <Column
          field="concepto"
          header="Concepto"
          sortable
        />

        <Column
          field="metodo_pago"
          header="Método"
          sortable
        />

        <Column
          field="monto"
          header="Monto"
          body={montoBody}
          sortable
        />

        <Column
          field="facturado"
          header="Facturado"
          body={facturadoBody}
          sortable
        />

      </DataTable>

    </Card>
  );
};

export default PagosHistorial;